import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Data } from '../data/Data';
import { useActualitesManager } from './actualiteContext';
import { useIntervenantsManager } from './intervenantContext';
import { useLotsCommerciauxManager } from './lotCommercialContext';
import { useLotsManager } from './lotContext';
import { useMessagesManager } from './messageContext';
import { useNotifications, useNotificationsManager } from './notificationContext';
import { useReglementsManager } from './reglementContext';
import { useStadesManager } from './stadeContext';
import { useUserManager } from './userContext';

export const AccessType = {
    forbidden: 0,
    prospect: 1,
    restricted: 2, // N'est plus utilis� dans la nouvelle version de l'espace personnel ; conserv� au cas o�
    client: 3
};

const AuthContext = createContext();
const AuthManagerContext = createContext();

export const AuthContextProvider = ({ children }) => {

    const idContact = sessionStorage.getItem("idContact");
    const location = useLocation();
    const navigate = useNavigate();

    const { dismiss, inform } = useNotificationsManager();
    const { errorMessages } = useNotifications();
    const { loadLotsCommerciaux, resetLotsCommerciaux } = useLotsCommerciauxManager();
    const { loadMessages, resetMessages } = useMessagesManager();
    const { resetActualites } = useActualitesManager();
    const { resetIntervenants } = useIntervenantsManager();
    const { resetLots } = useLotsManager();
    const { resetReglements } = useReglementsManager();
    const { resetStades } = useStadesManager();
    const { setUser } = useUserManager();

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessType, setAccessType] = useState(AccessType.forbidden);

    const disconnect = useCallback(() => {

        Data._logOut();
        sessionStorage.clear();

        setIsAuthenticated(false);
        resetActualites();
        resetIntervenants();
        resetLots();
        resetLotsCommerciaux();
        resetMessages();
        resetReglements();
        resetStades();
        setUser({});

        navigate("/");

    }, [navigate, resetActualites, resetLotsCommerciaux, resetMessages, resetIntervenants, resetLots, resetReglements, resetStades, setUser]);

    const connect = useCallback(async (login, password, accessKey) => {
        const id = inform("Chargement du profil...");
        const response = await Data._logIn(login, password, accessKey);
        dismiss(id);
        if (response.codeRetour === 0) {
            const objet = response.reponseAPIobjet;
            const { contact, typeAccesCapsule } = objet;
            sessionStorage.setItem("idContact", contact.idContactChiffre);//2776 : YD
            setAccessType(typeAccesCapsule);
            setIsAuthenticated(true);
            setUser(contact);
            if (typeAccesCapsule >= AccessType.restricted) loadLotsCommerciaux();
            if (typeAccesCapsule >= AccessType.client) loadMessages();
        }
        else {
            disconnect();
            return response;
        }
    }, [disconnect, dismiss, inform, loadLotsCommerciaux, loadMessages, setUser]);

    useEffect(() => {
        if (location.pathname.startsWith("/directAccess")) {
            const urlParams = new URLSearchParams(location.search);
            const key = urlParams.get("key");
            connect(null, null, key);
            navigate("/");
        }
    }, [connect, location, navigate]);

    useEffect(() => {
        if (isAuthenticated && errorMessages.some(message => message.code === 200)) // Session expir�e ; utilisateur non authentifi�
            disconnect();
    }, [disconnect, errorMessages, isAuthenticated]);

    useEffect(() => {
        if (!!idContact && !isAuthenticated)
            connect(idContact);
    }, [connect, isAuthenticated, idContact]);

    const value = useMemo(
        () => ({ accessType, isAuthenticated }),
        [accessType, isAuthenticated]
    );

    const managerValue = useMemo(
        () => ({ connect, disconnect }),
        [connect, disconnect]
    );

    return <AuthManagerContext.Provider value={managerValue}>
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    </AuthManagerContext.Provider>;

};

export const useAuth = () => {
    const data = React.useContext(AuthContext);
    if (data === undefined) throw new Error();
    const { accessType, isAuthenticated } = data;
    return { accessType, isAuthenticated };
};

export const useAuthManager = () => {
    const manager = React.useContext(AuthManagerContext);
    if (manager === undefined) throw new Error();
    const { connect, disconnect } = manager;
    return { connect, disconnect };
};